import { memo, useMemo, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import { CanvasEdgeSvg } from './CanvasEdgeSvg';
import type { CanvasSegment, EntityRectMap } from '../types';
import { resolveEndpointPosition, resolveEndpointDirection, getConnectorDirection } from '../types';
import { useCanvasStores } from '../stores/CanvasContext';
import { useCanvasContextMenus } from '../hooks/useCanvasContextMenus';
import { useCanvasConnectorOps } from '../hooks/useCanvasConnectorOps';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import type { ArrowMarkerType } from '../entityTypes';
import { DEFAULT_EDGE_COLOR } from '../entityTypes';

const EMPTY_EDGE_SET = new Set<string>();

export interface CanvasEdgeLayerProps {
  segments: CanvasSegment[];
  entityRectMap: EntityRectMap;
  depExplorerActive?: boolean;
  highlightedConnectorIds?: Set<string> | null;
}

// Marker shapes for SVG defs
function MarkerDef({ shape, color, id }: { shape: Exclude<ArrowMarkerType, 'none'>; color: string; id: string }) {
  switch (shape) {
    case 'arrow':
      return (
        <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={color} />
        </marker>
      );
    case 'open-arrow':
      return (
        <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 1 1 L 9 5 L 1 9" fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        </marker>
      );
    case 'triangle':
      return (
        <marker id={id} viewBox="0 0 12 12" refX="10" refY="6" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 12 6 L 0 12 Z" fill={color} />
        </marker>
      );
    case 'diamond':
      return (
        <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 5 0 L 10 5 L 5 10 L 0 5 Z" fill={color} />
        </marker>
      );
    case 'circle':
      return (
        <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <circle cx="5" cy="5" r="4" fill={color} />
        </marker>
      );
    case 'bar':
      return (
        <marker id={id} viewBox="0 0 4 10" refX="2" refY="5" markerWidth="4" markerHeight="8" orient="auto-start-reverse">
          <line x1="2" y1="0" x2="2" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </marker>
      );
    case 'square':
      return (
        <marker id={id} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <rect x="0.5" y="0.5" width="7" height="7" fill={color} />
        </marker>
      );
    default:
      return null;
  }
}

/** Collect all needed (shape, colorHex) marker combos from segments */
function collectNeededMarkers(segments: CanvasSegment[], selectedEdgeIds: Set<string>): Array<{ shape: Exclude<ArrowMarkerType, 'none'>; color: string; id: string }> {
  const markerSet = new Set<string>();
  const result: Array<{ shape: Exclude<ArrowMarkerType, 'none'>; color: string; id: string }> = [];

  function add(shape: ArrowMarkerType | undefined, color: string) {
    if (!shape || shape === 'none') return;
    const colorHex = color.replace('#', '');
    const id = `marker-${shape}-${colorHex}`;
    if (!markerSet.has(id)) {
      markerSet.add(id);
      result.push({ shape, color, id });
    }
  }

  // All non-'none' shapes
  const allShapes: Exclude<ArrowMarkerType, 'none'>[] = ['arrow', 'open-arrow', 'triangle', 'diamond', 'circle', 'bar', 'square'];

  // Always add selected (blue) markers for all shapes
  const selectedColor = '#3b82f6';
  for (const shape of allShapes) {
    add(shape, selectedColor);
  }

  // Always add amber (dep explorer) markers for all shapes
  const amberColor = '#f59e0b';
  for (const shape of allShapes) {
    add(shape, amberColor);
  }

  for (const seg of segments) {
    const baseColor = seg.strokeColor ?? DEFAULT_EDGE_COLOR;
    const isSelected = selectedEdgeIds.has(seg.id);
    // Normal color markers
    const arrowEnd = seg.arrowEnd ?? (seg.segmentType === 'bezier' || seg.segmentType === 'straight' ? 'arrow' : 'none');
    const arrowStart = seg.arrowStart ?? 'none';
    if (!isSelected) {
      add(arrowEnd, baseColor);
      add(arrowStart, baseColor);
    }
    // Hover state could also need the base color + 1 width offset, but marker color stays the same
    // We already added selected color markers above
  }

  return result;
}

function computeDirectionToward(from: { x: number; y: number }, to: { x: number; y: number }): { dx: number; dy: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { dx: dx / len, dy: dy / len };
}

function CanvasEdgeLayerInner({
  segments,
  entityRectMap,
  depExplorerActive = false,
  highlightedConnectorIds,
}: CanvasEdgeLayerProps) {
  const { store, dragStore, panZoomStore } = useCanvasStores();
  const { handleSegmentContextMenu } = useCanvasContextMenus();
  const { handleSegmentDragStart } = useCanvasConnectorOps();
  const connectionDrag = useSelector(dragStore, s => s.context.connectionDrag);
  const redockDrag = useSelector(dragStore, s => s.context.redockDrag);

  // Stable selector: only returns segment IDs that are in the selection, joined as string.
  // During box selection (entities selected, not edges), returns '' → no re-render.
  const segmentIdSet = useMemo(() => new Set(segments.map(s => s.id)), [segments]);
  const selectedEdgeIdsKey = useSelector(
    canvasSelectionStore,
    useCallback(
      (s: { context: { selectedEntityIds: string[] } }) => {
        const matches: string[] = [];
        for (const id of s.context.selectedEntityIds) {
          if (segmentIdSet.has(id)) matches.push(id);
        }
        return matches.join(',');
      },
      [segmentIdSet],
    ),
  );
  const selectedEdgeIds = useMemo(() => {
    if (!selectedEdgeIdsKey) return EMPTY_EDGE_SET;
    return new Set(selectedEdgeIdsKey.split(','));
  }, [selectedEdgeIdsKey]);

  const markers = useMemo(() => collectNeededMarkers(segments, selectedEdgeIds), [segments, selectedEdgeIds]);

  const handleLabelDrag = useCallback((segmentId: string, startOffsetX: number, startOffsetY: number, e: React.PointerEvent) => {
    const startX = e.clientX;
    const startY = e.clientY;

    const handleMove = (ev: PointerEvent) => {
      const zoom = panZoomStore.getSnapshot().context.zoom;
      const dx = (ev.clientX - startX) / zoom;
      const dy = (ev.clientY - startY) / zoom;
      store.send({
        type: 'connector.updateStyle',
        connectorId: segmentId,
        changes: { labelOffsetX: startOffsetX + dx, labelOffsetY: startOffsetY + dy },
      });
    };

    const handleUp = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  }, [store, panZoomStore]);

  return (
    <svg
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'visible',
      }}
    >
      <defs>
        {markers.map(m => (
          <MarkerDef key={m.id} shape={m.shape} color={m.color} id={m.id} />
        ))}
      </defs>

      {segments.map(segment => {
        const fromPos = resolveEndpointPosition(segment.from, entityRectMap);
        const toPos = resolveEndpointPosition(segment.to, entityRectMap);
        if (!fromPos || !toPos) return null;

        const fromDir = resolveEndpointDirection(segment.from, fromPos, toPos);
        const toDir = resolveEndpointDirection(segment.to, toPos, fromPos);

        const isDepHighlighted = depExplorerActive && highlightedConnectorIds?.has(segment.id);
        const isDepDimmed = depExplorerActive && highlightedConnectorIds !== null && highlightedConnectorIds !== undefined && !isDepHighlighted;

        return (
          <CanvasEdgeSvg
            key={segment.id}
            segmentId={segment.id}
            fromPoint={fromPos}
            toPoint={toPos}
            fromDirection={fromDir}
            toDirection={toDir}
            segmentType={segment.segmentType}
            isSelected={selectedEdgeIds.has(segment.id)}
            strokeColor={segment.strokeColor}
            strokeWidth={segment.strokeWidth}
            strokeStyle={segment.strokeStyle}
            arrowStart={segment.arrowStart}
            arrowEnd={segment.arrowEnd}
            label={segment.label}
            labelOffsetX={segment.labelOffsetX}
            labelOffsetY={segment.labelOffsetY}
            onLabelDrag={segment.label ? (e) => handleLabelDrag(segment.id, segment.labelOffsetX ?? 0, segment.labelOffsetY ?? 0, e) : undefined}
            onContextMenu={(e) => handleSegmentContextMenu(segment.id, e)}
            onClick={(e) => {
              if (e.shiftKey) {
                canvasSelectionStore.send({ type: 'entity.toggle', entityId: segment.id });
              } else {
                canvasSelectionStore.send({ type: 'entity.selectOne', entityId: segment.id });
              }
            }}
            onDragStart={(e) => handleSegmentDragStart(segment.id, e)}
            isDepHighlighted={!!isDepHighlighted}
            isDepDimmed={!!isDepDimmed}
          />
        );
      })}

      {/* New connection drag preview */}
      {connectionDrag && (() => {
        const fromDir = connectionDrag.fromEndpoint.side !== 'center'
          ? getConnectorDirection(connectionDrag.fromEndpoint.side)
          : computeDirectionToward(connectionDrag.fromPoint, connectionDrag.currentPoint);
        const toDir = computeDirectionToward(connectionDrag.currentPoint, connectionDrag.fromPoint);

        return (
          <CanvasEdgeSvg
            fromPoint={connectionDrag.fromPoint}
            toPoint={connectionDrag.currentPoint}
            fromDirection={fromDir}
            toDirection={toDir}
            segmentType="bezier"
            isDashed
          />
        );
      })()}

      {/* Segment redock drag preview */}
      {redockDrag && (() => {
        const isFromDrag = redockDrag.end === 'from';
        const fromPt = isFromDrag ? redockDrag.currentPoint : redockDrag.anchorPoint;
        const toPt = isFromDrag ? redockDrag.anchorPoint : redockDrag.currentPoint;

        let fromDir: { dx: number; dy: number };
        let toDir: { dx: number; dy: number };

        if (isFromDrag) {
          fromDir = computeDirectionToward(fromPt, toPt);
          toDir = redockDrag.anchorEndpoint.side !== 'center'
            ? getConnectorDirection(redockDrag.anchorEndpoint.side)
            : computeDirectionToward(toPt, fromPt);
        } else {
          fromDir = redockDrag.anchorEndpoint.side !== 'center'
            ? getConnectorDirection(redockDrag.anchorEndpoint.side)
            : computeDirectionToward(fromPt, toPt);
          toDir = computeDirectionToward(toPt, fromPt);
        }

        return (
          <CanvasEdgeSvg
            fromPoint={fromPt}
            toPoint={toPt}
            fromDirection={fromDir}
            toDirection={toDir}
            segmentType="bezier"
            isDashed
          />
        );
      })()}
    </svg>
  );
}

export const CanvasEdgeLayer = memo(CanvasEdgeLayerInner);
